import { GetAllBudgetVsActualConfigs } from './../models/budget-vs-actual-config-data.model';
import { Injectable } from '@angular/core';
import { DefaultAdminHandler } from 'app/details';
import { WidgetConfiguration } from '@feature/widget-configs';
import { FormDefinition, FormFieldDefinition } from '@feature/_app_modules/dynamic-forms/dynamic-form';
import { Observable, map, tap, combineLatest } from 'rxjs';
import { BudgetVsActualConfigData, SaveBudgetVsActualConfigRequest, BudgetVsActualWidgetHierarchyItem } from '../models/budget-vs-actual-config-data.model';
import { WidgetAdminHandler } from '@feature/widget-admin/services/widget-admin-handler';
import { BudgetVsActualConfigHttpService } from './budget-vs-actual-config-http.service';
import { mapWherePropertiesMatch } from '@common/services/object-mapper.service';
import {
  BvAPeriodSelection,
  BvAViewSelection
} from "@feature/_app_modules/budget-vs-actual/services/budget-vs-actual-enums";
import { BudgetVsActualHttpService } from './budget-vs-actual-http.service';

@Injectable()
export class BudgetVsActualWidgetAdminHandler implements WidgetAdminHandler {

    budgetVsActualConfigs: GetAllBudgetVsActualConfigs;
    allSelectedWidgetHierarchyItems: BudgetVsActualWidgetHierarchyItem[] = [];
    widgetHierarchyFields: BudgetVsActualWidgetHierarchyItem[] = [
      {SelectedValue: 'Schedule', WidgetConfigurationId: null},
      {SelectedValue: 'Cost Class', WidgetConfigurationId: null},
      {SelectedValue: 'Cost Category', WidgetConfigurationId: null}];

    constructor(private defaultAdminHandler: DefaultAdminHandler,
                private readonly bvaConfigHttpService: BudgetVsActualConfigHttpService,
                private readonly bvaHttpService: BudgetVsActualHttpService){};

    initialise(): boolean | Observable<boolean> {
      return true;
    };

    getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition | Observable<FormDefinition> {
      const widgetDefaultColumnsAndSortOrder = this.bvaHttpService.getdefaultcolumns(selectedConfig.id);

      const bvaConfigs = combineLatest(widgetDefaultColumnsAndSortOrder, this.bvaConfigHttpService.getConfiguration(selectedConfig.id)).pipe(
        map(([defaultColumnsAndSortOrder, response]) => {

          let formDefinition = this.defaultAdminHandler.getAdminForm(selectedConfig);

          formDefinition.fields.push(
            {
              key: 'defaultPeriod',
              label: 'Default Period',
              value: { value: response.BudgetVsActualConfig.DefaultPeriod},
              type: 'dropdown',
              dataKey: 'value',
              optionLabel: 'label',
              options: [
                { label: BvAPeriodSelection.CurrentYear, value: BvAPeriodSelection.CurrentYear },
                { label: BvAPeriodSelection.CurrentPeriod, value: BvAPeriodSelection.CurrentPeriod },
                { label: BvAPeriodSelection.CurrentYearAllPeriods, value: BvAPeriodSelection.CurrentYearAllPeriods }
              ]
            } as FormFieldDefinition
          );

          formDefinition.fields.push(
            {
              key: 'hierarchyVisibility',
              label: 'Group by',
              value: response.BudgetVsActualConfig.BudgetVsActualHierarchyItems,
              type: 'multiSelect',
              dataKey: 'SelectedValue',
              optionLabel: 'SelectedValue',
              options: this.widgetHierarchyFields
            } as FormFieldDefinition
          );

          formDefinition.fields.push(
            {
              key: 'defaultView',
              label: 'Default View',
              value: { value: response.BudgetVsActualConfig.DefaultView},
              type: 'dropdown',
              dataKey: 'value',
              optionLabel: 'label',
              options: [
                { label: BvAViewSelection.TableView, value: BvAViewSelection.TableView },
                { label: BvAViewSelection.ChartView, value: BvAViewSelection.ChartView }
              ]
            } as FormFieldDefinition
          );

          formDefinition.fields.push(
            {
              key: 'defaultColumnsAndOrder',
              label: 'Default Columns',
              value: response.BudgetVsActualConfig.BudgetVsActualDefaultColumnsAndSortOrderItems,
              type: 'multiSelect',
              dataKey: 'Field',
              optionLabel: 'FieldLabel',
              options: defaultColumnsAndSortOrder
            } as FormFieldDefinition
          );

          formDefinition.fields.push(
            {
              key: 'suppressExpenseCodesWithNoEntries',
              label: 'Expense codes',
              optionLabel: 'Suppress expense codes with no entries against them',
              value: response.BudgetVsActualConfig.SuppressExpenseCodesWithNoEntries,
              type: 'checkbox',
            } as FormFieldDefinition
          );

          formDefinition.fields.push(
            {
              key: 'includeCommitments',
              label: 'Commitments',
              optionLabel: 'Include commitments in calculations',
              value: response.BudgetVsActualConfig.IncludeCommitments,
              type: 'checkbox',
            } as FormFieldDefinition
          );

          return formDefinition;
      }));

      return bvaConfigs;
    }

    private createSaveRequest(selectedConfig: WidgetConfiguration, adminFormValue: any): SaveBudgetVsActualConfigRequest {

      const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);

      const saveBudgetVsActualConfigRequest = {} as SaveBudgetVsActualConfigRequest;

      saveBudgetVsActualConfigRequest.WidgetConfigModel = copyOfSelectedConfig;
      saveBudgetVsActualConfigRequest.BudgetVsActualModel = {
        WidgetConfigurationId: copyOfSelectedConfig.id,
        DefaultPeriod: adminFormValue.defaultPeriod.value,
        BudgetRunningLowThreshold: undefined,
        OverbudgetThreshold: undefined,
        BudgetVsActualHierarchyItems: adminFormValue.hierarchyVisibility,
        DefaultView: adminFormValue.defaultView.value,
        BudgetVsActualDefaultColumnsAndSortOrderItems: adminFormValue.defaultColumnsAndOrder,
        SuppressExpenseCodesWithNoEntries: adminFormValue.suppressExpenseCodesWithNoEntries,
        IncludeCommitments: adminFormValue.includeCommitments
      };

      saveBudgetVsActualConfigRequest.BudgetVsActualModel.BudgetVsActualHierarchyItems.forEach(item => {
        item.WidgetConfigurationId = copyOfSelectedConfig.id
      });

      return saveBudgetVsActualConfigRequest;
    }

    createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
      return this.bvaConfigHttpService.copyConfiguration(this.createSaveRequest(selectedConfig, adminFormValue)).pipe(map(s => true));
    }

    saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
      return this.bvaConfigHttpService.saveConfiguration(this.createSaveRequest(selectedConfig, adminFormValue)).pipe(map(s => true));
    }

    deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
      return this.defaultAdminHandler.deleteConfigFromAdminForm(selectedConfig, adminFormValue);
    }
}
